import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import {
  ClipboardList, Package, CheckCircle, AlertTriangle, Clock, TrendingUp,
  ArrowRight, Zap, Activity, ChevronLeft, ChevronRight, ArrowUpRight
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const PRIORITY_COLORS = { critical:'#EF4444', high:'#F97316', medium:'#F59E0B', low:'#10B981' };

const Badge = ({ status }) => (
  <span className={`badge badge-${status}`}>
    {status?.replace('_', ' ')}
  </span>
);

const PriorityDot = ({ p }) => (
  <span style={{ 
    display: 'inline-flex', 
    alignItems: 'center', 
    gap: 5, 
    fontSize: 11, 
    fontWeight: 700, 
    color: PRIORITY_COLORS[p] || 'var(--text-muted)',
    textTransform: 'capitalize'
  }}>
    <span style={{ 
      width: 6, 
      height: 6, 
      borderRadius: '50%', 
      background: PRIORITY_COLORS[p] || 'var(--text-muted)' 
    }} />
    {p}
  </span>
);

const Skeleton = ({ h = 20, w = '100%', style }) => (
  <div className="skeleton" style={{ height: h, width: w, borderRadius: 6, ...style }} />
);

const DashboardPagination = ({ current, total, onNext, onPrev, perPage = 5 }) => {
  const maxPage = Math.max(0, Math.ceil(total / perPage) - 1);
  if (total <= perPage) return null;

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between', 
      padding: '12px 16px', 
      borderTop: '1px solid var(--border)' 
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
        {current * perPage + 1}–{Math.min((current + 1) * perPage, total)} of {total}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button 
          className="btn btn-ghost btn-sm" 
          disabled={current === 0}
          onClick={onPrev}
          style={{ minHeight: 34, padding: '4px 10px' }}
          aria-label="Previous page"
        >
          <ChevronLeft size={15} />
        </button>
        <button 
          className="btn btn-ghost btn-sm" 
          disabled={current >= maxPage}
          onClick={onNext}
          style={{ minHeight: 34, padding: '4px 10px' }}
          aria-label="Next page"
        >
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobsPage, setJobsPage] = useState(0);
  const [perfPage, setPerfPage] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    dashboardAPI.stats()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const s = data?.job_stats || {};
  const as = data?.asset_stats || {};

  const isTech = user?.role === 'technician';

  const statCards = [
    { 
      label: isTech ? 'Assigned Operations' : 'Active Jobs', 
      value: s.active || 0, 
      icon: Activity, 
      color: '#0EA5E9', 
      bg: 'rgba(14, 165, 233, 0.12)', 
      accent: '#0EA5E9', 
      sub: `${s.in_progress || 0} in progress`,
      onClick: () => navigate(isTech ? '/technician' : '/jobs')
    },
    { 
      label: isTech ? 'My Monthly Output' : 'Completed This Month', 
      value: s.completed_this_month || 0, 
      icon: CheckCircle, 
      color: '#10B981', 
      bg: 'rgba(16, 185, 129, 0.12)', 
      accent: '#10B981', 
      sub: `${s.completed || 0} all-time resolved`,
      onClick: () => navigate(isTech ? '/technician' : '/jobs')
    },
    { 
      label: isTech ? 'Attention Required' : 'Overdue / SLA Alerts', 
      value: s.overdue || 0, 
      icon: AlertTriangle, 
      color: (s.overdue > 0 ? '#EF4444' : '#10B981'), 
      bg: (s.overdue > 0 ? 'rgba(239, 68, 68, 0.12)' : 'rgba(16, 185, 129, 0.12)'), 
      accent: (s.overdue > 0 ? '#EF4444' : '#10B981'), 
      sub: s.overdue > 0 ? `${s.overdue} SLA breach risks` : 'All schedules on track',
      onClick: () => navigate(isTech ? '/technician' : '/jobs')
    },
  ];

  if (!isTech) {
    statCards.push({ 
      label: 'Fleet Equipment', 
      value: as.total || 0, 
      icon: Package, 
      color: '#8B5CF6', 
      bg: 'rgba(139, 92, 246, 0.12)', 
      accent: '#8B5CF6', 
      sub: `${as.operational || 0} operational`,
      onClick: () => navigate('/assets')
    });
  }

  const weeklyData = (data?.weekly_trend || []).map(d => ({
    date: format(parseISO(d.date), 'EEE'),
    created: parseInt(d.created) || 0,
    completed: parseInt(d.completed) || 0,
  }));

  const assetPieData = [
    { name: 'Operational', value: parseInt(as.operational) || 0, color: '#10B981' },
    { name: 'Maintenance', value: parseInt(as.under_maintenance) || 0, color: '#F59E0B' },
    { name: 'Faulty', value: parseInt(as.faulty) || 0, color: '#EF4444' },
  ].filter(d => d.value > 0);

  const getGreetingTime = () => {
    const hours = new Date().getHours();
    if (hours < 12) return 'morning';
    if (hours < 17) return 'afternoon';
    return 'evening';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ── EXECUTIVE COCKPIT WELCOME BANNER ─────────────────── */}
      <div className="dashboard-welcome-banner animate-in">
        <div style={{
          position: 'absolute', top: -40, right: -40, width: 260, height: 260,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(14, 165, 233, 0.18) 0%, transparent 70%)',
          filter: 'blur(40px)', pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute', bottom: -50, right: 120, width: 200, height: 200,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
          filter: 'blur(50px)', pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Top Status & Date Pill Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'rgba(16, 185, 129, 0.14)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              padding: '3px 10px', borderRadius: 99,
              color: '#34D399', fontSize: 11, fontWeight: 700, letterSpacing: 0.4
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', background: '#10B981',
                boxShadow: '0 0 10px #10B981'
              }} />
              <span>LIVE TELEMETRY ACTIVE</span>
            </div>

            <div style={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: 12, fontWeight: 600 }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>

          {/* Greeting Headline with Role Badge */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
            <h1 className="dashboard-welcome-title" style={{ margin: 0 }}>
              Good {getGreetingTime()}, {user?.name?.split(' ')[0] || 'Technician'} 👋
            </h1>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
              background: 'rgba(14, 165, 233, 0.18)', border: '1px solid rgba(14, 165, 233, 0.35)',
              color: '#38BDF8', textTransform: 'uppercase', letterSpacing: 0.6
            }}>
              {user?.role?.replace('_', ' ') || 'USER'}
            </span>
          </div>

          <p style={{ color: '#94A3B8', fontSize: 13.5, maxWidth: 620, lineHeight: 1.45, margin: 0 }}>
            {s.overdue > 0 ? (
              <span style={{ color: '#FCA5A5', fontWeight: 600 }}>
                ⚠️ {s.overdue} overdue assignment{s.overdue > 1 ? 's' : ''} require immediate attention.
              </span>
            ) : (
              <span>
                All field assets & work orders are running within scheduled SLA tolerance ({s.active || 0} active).
              </span>
            )}
          </p>

          {/* Quick Action Toolbar */}
          <div className="dashboard-welcome-actions" style={{ display: 'flex', gap: 10, marginTop: 18, position: 'relative', zIndex: 1 }}>
            <button 
              className="btn btn-primary btn-sm" 
              onClick={() => navigate(isTech ? '/technician' : '/jobs')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
            >
              <ClipboardList size={15} />
              <span>{isTech ? 'My Assignments' : 'Operations Grid'}</span>
            </button>

            {!isTech && (
              <button 
                className="btn btn-sm" 
                onClick={() => navigate('/assets')}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#FFFFFF',
                  display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600
                }}
              >
                <Package size={15} />
                <span>Asset Fleet</span>
              </button>
            )}

            {!isTech && (
              <button 
                className="btn btn-sm hide-mobile" 
                onClick={() => navigate('/analytics')}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#FFFFFF',
                  display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600
                }}
              >
                <TrendingUp size={15} />
                <span>Compliance</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── KPI METRICS GRID (2x2 on phones, 4 cols on desktop) ─ */}
      <div className={`dashboard-kpi-grid ${isTech ? 'tech-view' : ''} animate-in stagger-1`}>
        {statCards.map((c) => (
          <div 
            key={c.label} 
            className="dashboard-kpi-card" 
            style={{ '--card-accent': c.accent }}
            onClick={c.onClick}
            role="button"
            tabIndex={0}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div className="stat-icon" style={{ background: c.bg, color: c.color }}>
                <c.icon size={19} color={c.color} />
              </div>
              <ArrowUpRight size={15} style={{ color: 'var(--text-muted)', opacity: 0.7 }} />
            </div>

            {loading ? (
              <>
                <Skeleton h={30} w={70} style={{ margin: '6px 0' }} />
                <Skeleton h={12} w={100} />
              </>
            ) : (
              <>
                <div className="stat-value">{c.value.toLocaleString()}</div>
                <div className="stat-label">{c.label}</div>
                <div className="stat-change" style={{ color: 'var(--text-muted)' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />
                  <span>{c.sub}</span>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* ── CHARTS ROW (Responsive) ────────────────────────── */}
      <div className="grid-2 animate-in stagger-2">
        {/* Weekly Trend */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">{isTech ? 'Operational Pulse' : 'Weekly Operations Pulse'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                7-day job dispatch vs completion
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, fontWeight: 600 }}>
              {[{ c: '#0EA5E9', l: isTech ? 'Assigned' : 'Created' }, { c: '#10B981', l: 'Completed' }].map(({ c, l }) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
                  <span style={{ color: 'var(--text-secondary)' }}>{l}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card-body" style={{ paddingTop: 10 }}>
            {loading ? (
              <Skeleton h={190} />
            ) : weeklyData.length ? (
              <ResponsiveContainer width="100%" height={190}>
                <AreaChart data={weeklyData} margin={{ top: 10, right: 10, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="gd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12, boxShadow: 'var(--shadow-md)' }} />
                  <Area type="monotone" dataKey="created" name={isTech ? 'Assigned' : 'Created'} stroke="#0EA5E9" fill="url(#gc)" strokeWidth={2.5} dot={false} />
                  <Area type="monotone" dataKey="completed" name="Completed" stroke="#10B981" fill="url(#gd)" strokeWidth={2.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state" style={{ padding: '36px 0' }}>
                <div className="empty-title">No weekly activity registered</div>
              </div>
            )}
          </div>
        </div>

        {/* Asset Status Pie (Admin/Manager) or System Status (Tech) */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">{isTech ? 'Station Telemetry' : 'Asset Health Distribution'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                {isTech ? 'Field status & synchronicity' : 'Fleet readiness across all categories'}
              </div>
            </div>
            {!isTech && (
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => navigate('/assets')}
                style={{ fontSize: 11, padding: '4px 8px' }}
              >
                Fleet <ArrowRight size={12} />
              </button>
            )}
          </div>
          <div className="card-body chart-card-body-donut" style={{ display: 'flex', alignItems: 'center', gap: 20, paddingTop: 10 }}>
            {loading ? (
              <Skeleton h={190} />
            ) : !isTech && assetPieData.length ? (
              <>
                <div style={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie 
                        data={assetPieData} 
                        cx={65} 
                        cy={65} 
                        innerRadius={44} 
                        outerRadius={65}
                        dataKey="value" 
                        paddingAngle={3}
                      >
                        {assetPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none'
                  }}>
                    <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)' }}>{as.total || 0}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>ASSETS</span>
                  </div>
                </div>

                <div className="pie-legend" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {assetPieData.map(d => {
                    const totalCount = parseInt(as.total) || 1;
                    const pct = Math.round((d.value / totalCount) * 100);
                    return (
                      <div key={d.name} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', borderRadius: 8, background: 'var(--bg-muted)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{d.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>{d.value}</span>
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : isTech ? (
              <div style={{ flex: 1, padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--bg-muted)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Active Dispatch Policy</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#10B981', marginTop: 2 }}>Real-time GPS & QR Evidence Enforced</div>
                </div>
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--bg-muted)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Telemetry Heartbeat</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>
                    Connected • {format(new Date(), 'HH:mm')} Local Time
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '36px 0', width: '100%' }}>
                <div className="empty-title">No asset data available</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── BOTTOM ROW: RECENT JOBS & TECHNICIAN PERFORMANCE ─ */}
      <div className="grid-2 animate-in stagger-3">
        {/* Recent Jobs */}
        <div className="card">
          <div className="card-header" style={{ paddingBottom: 14 }}>
            <div>
              <div className="card-title">{isTech ? 'My Active Work Orders' : 'Recent Field Operations'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                Real-time job queue
              </div>
            </div>
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={() => navigate(isTech ? '/technician' : '/jobs')}
              style={{ fontSize: 12 }}
            >
              View all <ArrowRight size={13} />
            </button>
          </div>

          <div className="table-wrap">
            {loading ? (
              <div style={{ padding: '20px 24px' }}>
                <Skeleton h={40} /><Skeleton h={40} style={{ marginTop: 8 }} />
              </div>
            ) : data?.recent_jobs?.length ? (
              <>
                <div className="recent-jobs-viewport">
                  {!isMobile ? (
                    /* Desktop / Tablet Table View */
                    <table>
                      <thead>
                        <tr>
                          <th>Job Details</th>
                          <th>Equipment</th>
                          <th>Status</th>
                          <th>Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recent_jobs.slice(jobsPage * 5, (jobsPage + 1) * 5).map(j => (
                          <tr key={j.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/jobs/${j.id}`)}>
                            <td>
                              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                                {j.job_number}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, maxWidth: 170 }} className="truncate">
                                {j.title}
                              </div>
                            </td>
                            <td>
                              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                {j.asset_name || '—'}
                              </span>
                            </td>
                            <td><Badge status={j.status} /></td>
                            <td><PriorityDot p={j.priority} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    /* Mobile Native Touch Cards View */
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {data.recent_jobs.slice(jobsPage * 5, (jobsPage + 1) * 5).map(j => (
                        <div 
                          key={j.id} 
                          className="recent-job-mobile-card"
                          onClick={() => navigate(`/jobs/${j.id}`)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{
                                fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                                color: 'var(--accent)', background: 'var(--accent-muted)',
                                padding: '2px 6px', borderRadius: 6
                              }}>
                                {j.job_number}
                              </span>
                              <PriorityDot p={j.priority} />
                            </div>
                            <Badge status={j.status} />
                          </div>

                          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35 }}>
                            {j.title}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, color: 'var(--text-muted)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Package size={13} /> {j.asset_name || 'Site General'}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 2, color: 'var(--accent)', fontWeight: 600 }}>
                              Open <ChevronRight size={13} />
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <DashboardPagination 
                  current={jobsPage} 
                  total={data.recent_jobs.length} 
                  onNext={() => setJobsPage(p => p + 1)}
                  onPrev={() => setJobsPage(p => p - 1)}
                />
              </>
            ) : (
              <div className="empty-state" style={{ padding: '36px 0' }}>
                <div className="empty-title">No jobs currently queued</div>
              </div>
            )}
          </div>
        </div>

        {/* Technician Performance / Team Overview */}
        <div className="card">
          <div className="card-header" style={{ paddingBottom: 14 }}>
            <div>
              <div className="card-title">{isTech ? 'My Field Productivity' : 'Technician Fleet Leaderboard'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                Job turnaround & resolution rate
              </div>
            </div>
          </div>
          <div className="card-body" style={{
            display: 'flex', flexDirection: 'column', gap: 12,
            paddingBottom: (data?.technician_performance?.length > 5 ? 0 : 20)
          }}>
            {loading ? (
              [1, 2, 3].map(i => <Skeleton key={i} h={46} />)
            ) : data?.technician_performance?.length ? (
              <>
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 12,
                  padding: (data.technician_performance.length > 5 ? '0 0 10px 0' : 0)
                }}>
                  {data.technician_performance.slice(perfPage * 5, (perfPage + 1) * 5).map((t, i) => {
                    const pct = t.total_jobs > 0 ? Math.round((t.completed / t.total_jobs) * 100) : 0;
                    const avatarColors = ['#0EA5E9', '#8B5CF6', '#10B981', '#F97316', '#EF4444'];
                    return (
                      <div key={t.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '8px 10px', borderRadius: 10, background: 'var(--bg-muted)'
                      }}>
                        <div className="avatar avatar-md" style={{
                          background: avatarColors[i % 5], fontSize: 12, fontWeight: 700, flexShrink: 0
                        }}>
                          {t.name?.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{t.name}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
                              {t.completed}/{t.total_jobs} jobs
                            </span>
                          </div>
                          <div className="progress-bar" style={{ height: 6 }}>
                            <div 
                              className="progress-fill" 
                              style={{ 
                                width: `${pct}%`, 
                                background: pct >= 75 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#0EA5E9' 
                              }} 
                            />
                          </div>
                        </div>
                        <div style={{
                          fontSize: 12, fontWeight: 800, color: 'var(--text)',
                          minWidth: 38, textAlign: 'right', fontFamily: 'var(--font-mono)'
                        }}>
                          {pct}%
                        </div>
                      </div>
                    );
                  })}
                </div>
                <DashboardPagination 
                  current={perfPage} 
                  total={data.technician_performance.length} 
                  onNext={() => setPerfPage(p => p + 1)}
                  onPrev={() => setPerfPage(p => p - 1)}
                />
              </>
            ) : (
              <div className="empty-state" style={{ padding: '36px 0' }}>
                <div className="empty-title">No field technicians recorded</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
